import React, { useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";

import {
  Module,
  ModuleBody,
  ModuleFooter,
  ModuleHeader,
  Wrapper,
  Button,
} from "@wfp/ui";

import Table from "components/CountryAdmin/CountryAdminContent/Table/Table";
import Filtering from "components/CountryAdmin/CountryAdminContent/Table/Filtering";
import { valueLinkCell } from "components/CountryAdmin/CountryAdminContent/Table/Utils";
import CsvImportModal from "../CsvImportModal";

import { CountryAdminLabel } from "SCConstants";

import {
  getPendingAdminVendors,
  requestPendingAdminVendors,
  performVendorAction,
} from "data-handler/ducks/vendors";

import "../AllVendors/_content.scss";

const NewVendors = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const pendingVendorList = useSelector(getPendingAdminVendors);

  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filtersState, setFilters] = useState([]);
  const [csvImportModalOpen, setCsvImportModalOpen] = useState(false);

  const { count = 0, ids = [] } = pendingVendorList;
  const data = ids.map((id) => pendingVendorList.results[id]);

  const filterProps = {
    fields: [
      {
        name: "vendor_category",
        label: CountryAdminLabel.find(
          (e) => e.name === "countryAdmin.vendorType"
        ).label,
      },
      {
        name: "name",
        label: CountryAdminLabel.find(
          (e) => e.name === "countryAdmin.vendorName"
        ).label,
      },
      {
        name: "ref_number",
        label: CountryAdminLabel.find((e) => e.name === "countryAdmin.refNo")
          .label,
      },
      {
        name: "adm_4_name",
        label: intl.formatMessage({
          id: "CountryAdmin.adminFour",
          defaultMessage: "Admin 4",
        }),
      },
      {
        name: "adm_3_name",
        label: intl.formatMessage({
          id: "CountryAdmin.adminThree",
          defaultMessage: "Admin 3",
        }),
      },
      {
        name: "adm_2_name",
        label: intl.formatMessage({
          id: "CountryAdmin.adminTwo",
          defaultMessage: "Admin 2",
        }),
      },
      {
        name: "adm_1_name",
        label: intl.formatMessage({
          id: "CountryAdmin.adminOne",
          defaultMessage: "Admin 1",
        }),
      },
      {
        name: "country_name",
        label: intl.formatMessage({
          id: "CountryAdmin.country",
          defaultMessage: "Country",
        }),
      },
    ],
    onSubmit: (e) => {
      const filters = Object.keys(e).map((key) => ({ id: key, value: e[key] }));
      setFilters(filters);
      loadPage(pageSize, 1, "", filters);
    },
    defaults: filtersState.reduce((a, x) => ({ ...a, [x?.id]: x?.value }), {}),
  };

  const loadPage = (size, idx, ordering, filters = filtersState) => {
    setPageSize(size);
    setPageNum(idx);
    dispatch(requestPendingAdminVendors(size, idx, ordering, filters));
  };

  const getActionsLabels = (vendorState) => {
    switch (vendorState) {
      case "new":
        return [
          {
            label: CountryAdminLabel.find(
              (e) => e.name === "countryAdmin.promote"
            ).label,
            action: "adminDefine",
          },
        ];
      case "school_defined":
        return [
          {
            label: CountryAdminLabel.find(
              (e) => e.name === "countryAdmin.promote"
            ).label,
            action: "promote",
          },
          {
            label: CountryAdminLabel.find(
              (e) => e.name === "countryAdmin.deactivate"
            ).label,
            action: "deactivateSchoolDefined",
          },
        ];
      case "admin_approved":
        return [
          {
            label: CountryAdminLabel.find(
              (e) => e.name === "countryAdmin.deactivate"
            ).label,
            action: "deactivateAdminApproved",
          },
        ];
      case "deactivated":
        return [
          {
            label: CountryAdminLabel.find(
              (e) => e.name === "countryAdmin.activate"
            ).label,
            action: "activate",
          },
        ];
      default:
        return [];
    }
  };

  const actionButtons = useCallback(
    ({ row }) => {
      const actions = getActionsLabels(row["original"]["state"]);
      const vendorId = row["original"]["id"];

      return (
        <div className="vendors-action-buttons">
          {actions.map((item) => (
            <Button
              className="action-button"
              onClick={() => {
                dispatch(performVendorAction(item.action, vendorId));
              }}
            >
              {item.label}
            </Button>
          ))}
        </div>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const columns = useMemo(() => {
    return [
      {
        Header: (
          <FormattedMessage
            id="SchoolVendorsShow.VendorType"
            defaultMessage="Vendor type"
          />
        ),
        accessor: "vendor_category.name",
      },
      {
        Header: (
          <FormattedMessage
            id="SchoolVendorsShow.VendorName"
            defaultMessage="Vendor name"
          />
        ),
        accessor: "name",
        Cell: ({ cell: { row, value } }) =>
          valueLinkCell(
            value,
            `/countryAdmin/vendors/newVendors/${row["original"]["id"]}`
          ),
      },
      {
        Header: (
          <FormattedMessage
            id="SchoolVendorsShow.ref-no"
            defaultMessage="Reference no."
          />
        ),
        accessor: "ref_number",
      },
      {
        Header: intl.formatMessage({
          id: "CountryAdmin.adminFour",
          defaultMessage: "Admin 4",
        }),
        accessor: "adm_4.name",
      },
      {
        Header: intl.formatMessage({
          id: "CountryAdmin.adminThree",
          defaultMessage: "Admin 3",
        }),
        accessor: "adm_3.name",
      },
      {
        Header: intl.formatMessage({
          id: "CountryAdmin.adminTwo",
          defaultMessage: "Admin 2",
        }),
        accessor: "adm_2.name",
      },
      {
        Header: intl.formatMessage({
          id: "CountryAdmin.adminOne",
          defaultMessage: "Admin 1",
        }),
        accessor: "adm_1.name",
      },
      {
        Header: intl.formatMessage({
          id: "CountryAdmin.country",
          defaultMessage: "Country",
        }),
        accessor: "country.name",
      },
      {
        Header: intl.formatMessage({
          id: "CountryAdmin.action",
          defaultMessage: "Action",
        }),
        accessor: "approve",
        Cell: actionButtons,
        width: 250,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionButtons]);

  const tableProps = {
    columns,
    data,
    pageNum,
    pageSize,
    totalItems: count,
    loadPage,
  };

  const csvImportModalProps = {
    csvImportModalOpen,
    setCsvImportModalOpen,
  };

  return (
    <Wrapper pageWidth="lg" spacing="md" background="lighter" className="table">
      <Module>
        <ModuleHeader>
          <FormattedMessage
            id="countryAdmin.newVendors"
            defaultMessage="New Vendors"
          />
        </ModuleHeader>
        <ModuleBody>
          <Filtering {...filterProps} />
          <Table {...tableProps} showSort />
          <CsvImportModal {...csvImportModalProps} />
        </ModuleBody>
        <ModuleFooter>
          <div className="vendors-action-buttons">
            <Link exact="true" to={`/countryAdmin/vendors/create`}>
              <Button>
                <FormattedMessage
                  id="countryAdmin.createNewVendor"
                  defaultMessage="Create new vendor"
                />
              </Button>
            </Link>
            <Button
              className="wfp--btn--secondary"
              onClick={() => {
                setCsvImportModalOpen(true);
              }}
            >
              <FormattedMessage
                id="modal.importCsv"
                defaultMessage="Import CSV"
              />
            </Button>
          </div>
        </ModuleFooter>
      </Module>
    </Wrapper>
  );
};

export default NewVendors;
